/**
 * components/driver/EndOfDayDepositCard.jsx · v2.7.56+E33
 *
 * End-of-Day Deposit card for the driver dashboard. Drivers punch in the
 * cash / Square / Cash-App / Venmo breakdown + tips + memo. Total is
 * auto-computed in real time and shown as two rolling banners (Total
 * Deposit + Total Shift Earnings).
 *
 * Receipt image upload is OPTIONAL — leaving it blank does NOT block
 * submission. On submit the card also shows the driver's last 5 deposits
 * so they can spot ESCALATED rows and follow up.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Wallet, Banknote, Smartphone, Zap, HandCoins, Upload, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, ShieldCheck, Building2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtUsd } from "@/lib/format";

const DEFAULT_BREAKDOWN = {
  cash_amount: "",
  square_amount: "",
  cash_app_amount: "",
  venmo_amount: "",
  tips_amount: "",
  memo: "",
};

const parseNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

export default function EndOfDayDepositCard({ defaultOpen = false } = {}) {
  const location = useLocation();
  const [open, setOpen] = useState(!!defaultOpen);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(DEFAULT_BREAKDOWN);
  const [receiptFile, setReceiptFile] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // v2.7.56+E33.4 · The Memo/Notes textarea sits directly above the Total
  // Deposit tiles. Drivers reported it was pushing the totals + submit
  // button below the fold and hindering meter access. Hide by default;
  // reveal on demand via a tiny "+ Add note (optional)" toggle. All
  // existing memo state / submit behaviour is preserved — this is a
  // pure visibility flag.
  const [showMemo, setShowMemo] = useState(false);

  // v2.7.56+E33.2 · Auto-open + scroll into view when the driver arrives with
  // the `#eod` URL hash. Uses react-router's useLocation() so this fires on
  // `<Link>` navigation (react-router calls history.pushState which does NOT
  // dispatch the native `hashchange` event — a plain window listener would
  // silently miss deep-links).
  useEffect(() => {
    if (location.hash === "#eod") {
      setOpen(true);
      setTimeout(() => {
        const el = document.getElementById("eod");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  }, [location.hash]);

  const totalDeposit = useMemo(() => (
    parseNum(form.cash_amount) +
    parseNum(form.square_amount) +
    parseNum(form.cash_app_amount) +
    parseNum(form.venmo_amount)
  ), [form.cash_amount, form.square_amount, form.cash_app_amount, form.venmo_amount]);

  const totalShiftEarnings = useMemo(() => (
    totalDeposit + parseNum(form.tips_amount)
  ), [totalDeposit, form.tips_amount]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/driver/deposits/me?limit=5");
      setHistory(r.data?.items || []);
    } catch (e) {
      // Silent — the form still works even if history endpoint is unreachable.
      console.warn("[eod] history load failed:", e?.message);
    } finally {
      setHistoryLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (open && !historyLoaded) loadHistory();
  }, [open, historyLoaded, loadHistory]);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const canSubmit = totalDeposit > 0 || parseNum(form.tips_amount) > 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Enter at least one deposit or tip amount.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        cash_amount:      parseNum(form.cash_amount),
        square_amount:    parseNum(form.square_amount),
        cash_app_amount:  parseNum(form.cash_app_amount),
        venmo_amount:     parseNum(form.venmo_amount),
        tips_amount:      parseNum(form.tips_amount),
        memo:             form.memo || "",
      };
      const res = await api.post("/driver/deposits", payload);
      const deposit = res.data;
      toast.success(`Deposit submitted · ${fmtUsd(deposit.total_deposit_submitted)} + ${fmtUsd(deposit.tips_amount)} tips`);

      // Optional receipt upload — if the driver picked a file, upload it now
      // in a separate call. Failure does NOT roll back the deposit.
      if (receiptFile) {
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", receiptFile);
          await api.post(`/driver/deposits/${deposit.id}/receipt`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Receipt attached");
        } catch (e) {
          const msg = e?.response?.data?.detail || e?.message || "unknown";
          toast.warning(`Deposit saved but receipt upload failed: ${msg}`);
        } finally {
          setUploading(false);
        }
      }

      setForm(DEFAULT_BREAKDOWN);
      setReceiptFile(null);
      setHistoryLoaded(false);   // trigger reload
      loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Submit failed";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card id="eod" className="bg-zinc-900 border-zinc-800 mb-3 scroll-mt-4" data-testid="eod-deposit-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors"
        data-testid="eod-toggle"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center flex-shrink-0">
            <Wallet className="h-5 w-5 text-emerald-300"/>
          </div>
          <div>
            <div className="text-sm font-bold text-zinc-100">End of Day / Deposit</div>
            <div className="text-[11px] text-zinc-400">Record cash, card, and app deposits + tips</div>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-400"/> : <ChevronDown className="h-4 w-4 text-zinc-400"/>}
      </button>

      {open && (
        <div className="p-4 pt-0 border-t border-zinc-800">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <MoneyField
              icon={<Banknote className="h-3.5 w-3.5"/>}
              label="Cash Deposit"
              testid="eod-cash"
              value={form.cash_amount}
              onChange={update("cash_amount")}
            />
            <MoneyField
              icon={<Zap className="h-3.5 w-3.5"/>}
              label="Square Deposit"
              testid="eod-square"
              value={form.square_amount}
              onChange={update("square_amount")}
            />
            <MoneyField
              icon={<Smartphone className="h-3.5 w-3.5"/>}
              label="Cash App"
              testid="eod-cash-app"
              value={form.cash_app_amount}
              onChange={update("cash_app_amount")}
            />
            <MoneyField
              icon={<Smartphone className="h-3.5 w-3.5"/>}
              label="Venmo"
              testid="eod-venmo"
              value={form.venmo_amount}
              onChange={update("venmo_amount")}
            />
            <MoneyField
              icon={<HandCoins className="h-3.5 w-3.5"/>}
              label="Tips Received"
              testid="eod-tips"
              value={form.tips_amount}
              onChange={update("tips_amount")}
              accent="amber"
            />
            <div className="flex flex-col justify-end">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Receipt (optional)</Label>
              <label className="cursor-pointer inline-flex items-center justify-center gap-2 h-10 border border-zinc-700 rounded bg-zinc-950 text-xs text-zinc-300 hover:bg-zinc-800" data-testid="eod-receipt-label">
                <Upload className="h-3.5 w-3.5"/>
                {receiptFile ? receiptFile.name.slice(0, 24) : "Choose image / PDF"}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  data-testid="eod-receipt-input"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>
          </div>

          <div className="mt-4">
            {/* v2.7.56+E33.4 · Memo hidden by default. Tap the toggle to
                expand — keeps totals + submit button above the fold on
                small driver screens. Textarea itself + submit payload
                path is unchanged. */}
            {!showMemo ? (
              <button
                type="button"
                onClick={() => setShowMemo(true)}
                className="text-[11px] text-zinc-400 hover:text-emerald-300 underline underline-offset-2 transition"
                data-testid="eod-memo-toggle-show"
              >
                + Add note (optional)
              </button>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Label htmlFor="eod-memo" className="text-[10px] uppercase tracking-widest text-zinc-400">Memo / Notes</Label>
                  <button
                    type="button"
                    onClick={() => setShowMemo(false)}
                    className="text-[10px] text-zinc-500 hover:text-zinc-300 transition"
                    data-testid="eod-memo-toggle-hide"
                  >
                    Hide
                  </button>
                </div>
                <Textarea
                  id="eod-memo"
                  data-testid="eod-memo"
                  rows={2}
                  value={form.memo}
                  onChange={update("memo")}
                  placeholder="e.g. Transferred from Cash App tag $driver123"
                  className="mt-1 bg-zinc-950 border-zinc-700 text-sm"
                />
              </>
            )}
          </div>

          {/* Running totals */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <TotalTile
              label="Total Deposit"
              value={totalDeposit}
              accent="emerald"
              testid="eod-total-deposit"
            />
            <TotalTile
              label="Total Shift Earnings"
              value={totalShiftEarnings}
              accent="amber"
              testid="eod-total-earnings"
            />
          </div>

          {/* v2.7.63+E46.3 · Compliance Ledger · Driver Deposit Verification
              Explicit Tips / Profits split for bank-reconciliation cross-check.
              Additive model per operator confirmation:
                Total Expected Deposit = Tips + Profits
                  · Tips    = tips_amount (100% driver's, reported for payroll)
                  · Profits = cash + square + cash_app + venmo (company revenue)
              This banner is READ-ONLY — it derives entirely from the fields
              above so any keystroke re-computes it. */}
          <div
            className="mt-4 p-3 rounded-lg border border-sky-400/40 bg-gradient-to-br from-sky-500/10 to-zinc-950"
            data-testid="eod-compliance-card"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <ShieldCheck className="h-3.5 w-3.5 text-sky-300"/>
              <span className="text-[10px] uppercase tracking-widest text-sky-200 font-bold">
                Compliance Breakdown · Bank Reconciliation
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <ComplianceTile
                icon={<Wallet className="h-3.5 w-3.5"/>}
                label="Total Expected"
                value={totalShiftEarnings}
                tone="sky"
                testid="eod-compliance-total"
                note="Sum handed over"
              />
              <ComplianceTile
                icon={<HandCoins className="h-3.5 w-3.5"/>}
                label="Tips"
                value={parseNum(form.tips_amount)}
                tone="amber"
                testid="eod-compliance-tips"
                note="Driver keeps 100%"
              />
              <ComplianceTile
                icon={<Building2 className="h-3.5 w-3.5"/>}
                label="Profits"
                value={totalDeposit}
                tone="emerald"
                testid="eod-compliance-profits"
                note="Company revenue"
              />
            </div>
            <div className="text-[10px] text-zinc-500 mt-2 leading-relaxed" data-testid="eod-compliance-note">
              <span className="text-sky-300 font-bold">Total Expected</span> is the amount you&apos;re
              turning in tonight. <span className="text-amber-300 font-bold">Tips</span> are yours to
              keep (100%, reported for payroll/tax). <span className="text-emerald-300 font-bold">
              Profits</span> hit the company account and are cross-referenced against tomorrow&apos;s
              bank statement.
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || busy || uploading}
            className="w-full mt-4 h-11 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-extrabold"
            data-testid="eod-submit"
          >
            {busy ? "Submitting…" : uploading ? "Uploading receipt…" : "SUBMIT END-OF-DAY DEPOSIT"}
          </Button>

          {/* Recent submissions — surfaces ESCALATED rows so drivers can act on discrepancies */}
          {history.length > 0 && (
            <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="eod-history">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Recent Submissions</div>
              <div className="space-y-1.5">
                {history.map((d) => <HistoryRow key={d.id} row={d}/>)}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function MoneyField({ icon, label, testid, value, onChange, accent = "zinc" }) {
  const ring = accent === "amber" ? "focus-within:ring-amber-400/40" : "focus-within:ring-emerald-400/40";
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
        <span className="text-zinc-500">{icon}</span>{label}
      </Label>
      <div className={`relative mt-1 rounded ${ring} focus-within:ring-2 transition`}>
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-bold pointer-events-none">$</span>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={onChange}
          placeholder="0.00"
          data-testid={testid}
          className="bg-zinc-950 border-zinc-700 h-10 pl-5 meter-display"
        />
      </div>
    </div>
  );
}

function TotalTile({ label, value, accent, testid }) {
  const tone = accent === "emerald"
    ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-300"
    : "bg-amber-500/10 border-amber-400/40 text-amber-300";
  return (
    <div className={`rounded-lg border p-3 ${tone}`} data-testid={`${testid}-tile`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="meter-display text-2xl font-extrabold mt-1" data-testid={testid}>
        {fmtUsd(value || 0)}
      </div>
    </div>
  );
}

function ComplianceTile({ icon, label, value, tone, note, testid }) {
  const styles = {
    sky:     "bg-sky-500/15    border-sky-400/50    text-sky-200",
    amber:   "bg-amber-500/10  border-amber-400/40  text-amber-200",
    emerald: "bg-emerald-500/10 border-emerald-400/40 text-emerald-200",
  }[tone] || "bg-zinc-800 border-zinc-700 text-zinc-200";
  return (
    <div className={`rounded-md border p-2 ${styles}`} data-testid={`${testid}-tile`}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest opacity-80">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="meter-display text-lg font-extrabold mt-0.5" data-testid={testid}>
        {fmtUsd(value || 0)}
      </div>
      <div className="text-[9px] opacity-70 mt-0.5 truncate">{note}</div>
    </div>
  );
}

function HistoryRow({ row }) {
  const status = row.status || "PENDING";
  const chip = {
    PENDING:   "bg-zinc-700/50 text-zinc-300 border-zinc-600",
    CLOSED:    "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
    ESCALATED: "bg-red-500/15 text-red-300 border-red-400/40",
  }[status] || "bg-zinc-700/50 text-zinc-300 border-zinc-600";
  const icon = status === "CLOSED" ? <CheckCircle2 className="h-3 w-3"/>
    : status === "ESCALATED" ? <AlertTriangle className="h-3 w-3"/>
    : null;
  return (
    <div className="flex items-center justify-between p-2 rounded bg-zinc-950 border border-zinc-800 text-xs" data-testid={`eod-history-row-${row.id}`}>
      <div>
        <div className="text-zinc-200 font-bold">{row.shift_date}</div>
        {row.discrepancy_amount > 0 && (
          <div className="text-[10px] text-red-300">
            Variance {fmtUsd(row.discrepancy_amount)} · verified {fmtUsd(row.verified_deposit_amount || 0)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="meter-display text-zinc-300">{fmtUsd(row.total_deposit_submitted || 0)}</span>
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${chip}`}>
          {icon}{status}
        </span>
      </div>
    </div>
  );
}
